/**
 * Where a number on a dashboard leads.
 *
 * Every count on Početna and every tile on Analitika opens the list behind it,
 * already filtered to what was counted — "22 van roka" lands on those 22, not
 * on the whole inbox with a filter left for the reader to find. That only
 * holds while the links and the list agree on what the URL means, so the
 * links are built here, once, rather than spelled out at each tile.
 *
 * ## The parameters
 *
 * - `tab` is one of the list's own quick tabs, named the way the tabs are.
 * - Everything else carries the API's own filter name — `status`, `type`,
 *   `from`, `to`, `plantId`, `unassigned` — the convention `workCenterId` and
 *   `search` already set for the links from the search palette. One name for
 *   a filter in the address bar and on the wire is one fewer mapping for the
 *   list to keep.
 *
 * Absent values are left out rather than written empty, so a link reads the
 * way somebody would type it.
 */

/** The list's quick tabs, as its URL spells them. */
export type SubmissionsTab =
  | 'otvorene'
  | 'nove'
  | 'u-radu'
  | 'van-roka'
  | 'dodijeljene-meni'
  | 'moje'

export interface SubmissionsLink {
  tab?: SubmissionsTab
  /** One status, or several — the API takes a list. */
  status?: string | string[]
  type?: string
  /** Days on the company's calendar, `YYYY-MM-DD` — the same pair the period picker sends. */
  from?: string
  to?: string
  plantId?: string | null
  /** Open and on nobody's desk. */
  unassigned?: boolean
}

export function submissionsHref(link: SubmissionsLink = {}): string {
  const params = new URLSearchParams()

  if (link.tab) params.set('tab', link.tab)
  if (link.status) params.set('status', ([] as string[]).concat(link.status).join(','))
  if (link.type) params.set('type', link.type)
  if (link.from) params.set('from', link.from)
  if (link.to) params.set('to', link.to)
  if (link.plantId) params.set('plantId', link.plantId)
  if (link.unassigned) params.set('unassigned', 'true')

  const query = params.toString()

  return query ? `/prijave?${query}` : '/prijave'
}

/** One report. */
export function submissionHref(id: string): string {
  return `/prijave/${encodeURIComponent(id)}`
}

/** This month's 5S board — the "Ovaj mjesec" view of `/5s`. */
export const FIVE_S_BOARD = '/5s'

/** A walk of one machine, with the machine already chosen. */
export function newWalkHref(workCenterId: string): string {
  return `/5s/nova?workCenterId=${encodeURIComponent(workCenterId)}`
}

/** One walk. */
export function walkHref(id: string): string {
  return `/5s/${encodeURIComponent(id)}`
}

export const TASKS = '/zadaci'
